/**
 * InputMealMatrix - Renders the 7-column meal intake table for the monthly report.
 *
 * Displays one row per date that has at least one meal session recorded.
 * Rows where ALL meal columns are empty are hidden.
 * Columns: Date, Breakfast, Lunch, Tea Time, Dinner, Fruits, Snack.
 *
 * Each meal cell shows sessions with time and food items (name, amount, unit).
 * Multiple sessions in the same cell are separated by a subtle divider.
 * Note-only sessions (empty foodItems, non-empty notes) display the notes text.
 * Sessions with both food and notes display food items AND notes.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7
 */

import React from 'react';
import type { DailyInputRow, MealSession } from '../types';

export interface InputMealMatrixProps {
  mealMatrix: DailyInputRow[];
}

/** Meal category keys in display order */
const MEAL_KEYS = ['breakfast', 'lunch', 'teaTime', 'dinner', 'fruits', 'snack'] as const;

/** Column headers matching the 7-column layout */
const COLUMN_HEADERS = ['Date', 'Breakfast', 'Lunch', 'Tea Time', 'Dinner', 'Fruits', 'Snack'];

/** Check if a row has any data across all meal columns */
function rowHasData(row: DailyInputRow): boolean {
  return MEAL_KEYS.some((key) => row.meals[key].length > 0);
}

/**
 * Format a date key (YYYY-MM-DD) to a more readable format.
 */
function formatDateLabel(dateKey: string): string {
  const d = new Date(dateKey + 'T00:00:00');
  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'short' });
  const weekday = d.toLocaleString('en-US', { weekday: 'short' });
  return `${weekday}, ${month} ${day}`;
}

/**
 * Renders a single meal session within a cell.
 */
const MealSessionCell: React.FC<{ session: MealSession }> = ({ session }) => {
  const hasFood = session.foodItems.length > 0;
  const hasNotes = session.notes.trim().length > 0;

  return (
    <div style={{ padding: '4px 0' }}>
      <div style={{ fontSize: '0.75rem', color: '#888', fontWeight: 500, marginBottom: '2px' }}>
        {session.timeLabel}
      </div>

      {hasFood && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {session.foodItems.map((item, idx) => (
            <div key={idx} style={{ fontSize: '0.82rem', color: '#333', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#5A46AA', flexShrink: 0 }} />
              <span>{item.name}</span>
              <span style={{ color: '#999', fontSize: '0.75rem' }}>{item.amount} {item.unit}</span>
            </div>
          ))}
        </div>
      )}

      {hasNotes && (
        <div
          style={{
            fontSize: '0.78rem',
            color: '#666',
            fontStyle: 'italic',
            marginTop: hasFood ? '4px' : '0',
            padding: '3px 6px',
            backgroundColor: '#fffbe6',
            borderRadius: '4px',
            borderLeft: '2px solid #ffc107',
          }}
        >
          {session.notes}
        </div>
      )}
    </div>
  );
};

/**
 * Renders the content of a meal cell, which may contain zero or more sessions.
 */
const MealCellContent: React.FC<{ sessions: MealSession[] }> = ({ sessions }) => {
  if (sessions.length === 0) {
    return (
      <span style={{ color: '#ccc', fontSize: '0.8rem' }}>—</span>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {sessions.map((session, idx) => (
        <React.Fragment key={session.inputId}>
          {idx > 0 && (
            <div
              style={{ borderTop: '1px dashed #e8e8e8', margin: '2px 0' }}
              aria-hidden="true"
            />
          )}
          <MealSessionCell session={session} />
        </React.Fragment>
      ))}
    </div>
  );
};

const InputMealMatrix: React.FC<InputMealMatrixProps> = ({ mealMatrix }) => {
  // Filter out rows where ALL meal columns are empty
  const filteredRows = mealMatrix.filter(rowHasData);

  if (filteredRows.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: '#fafafa',
        borderRadius: '12px',
      }}>
        <p style={{ color: '#999', fontStyle: 'italic', margin: 0 }}>
          No meal data available for this month.
        </p>
      </div>
    );
  }

  return (
    <div className="input-meal-matrix" style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #f0f0f0' }}>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}
        aria-label="Monthly meal intake matrix"
      >
        <thead>
          <tr>
            {COLUMN_HEADERS.map((header, idx) => (
              <th
                key={header}
                scope="col"
                style={{
                  padding: '12px 14px',
                  backgroundColor: '#f8f9fa',
                  textAlign: 'left',
                  verticalAlign: 'middle',
                  borderBottom: '2px solid #e9ecef',
                  color: '#555',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                  whiteSpace: 'nowrap',
                  ...(idx === 0 ? { width: '120px' } : {}),
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredRows.map((row, rowIdx) => (
            <tr
              key={row.dateKey}
              style={{
                backgroundColor: rowIdx % 2 === 0 ? '#fff' : '#fafbfc',
                transition: 'background-color 0.15s',
              }}
            >
              <td
                style={{
                  padding: '10px 14px',
                  verticalAlign: 'top',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  color: '#333',
                  borderBottom: '1px solid #f0f0f0',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatDateLabel(row.dateKey)}
              </td>
              {MEAL_KEYS.map((mealKey) => (
                <td
                  key={mealKey}
                  style={{
                    padding: '10px 14px',
                    verticalAlign: 'top',
                    borderBottom: '1px solid #f0f0f0',
                    borderLeft: '1px solid #f5f5f5',
                  }}
                >
                  <MealCellContent sessions={row.meals[mealKey]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InputMealMatrix;
